/* Home hero: an animated inspection line drawn on a canvas.
 *
 * Parts ride several conveyor lanes past a scanning beam. Each part is
 * graded as it crosses the beam; rejects get a red bracket and drop off the
 * belt. The beam follows the cursor horizontally. The HUD counters are
 * updated through refs so the animation never re-renders React.
 */

import React from 'react';
import { HERO_STATS } from './data.js';
import { ContactButton, scrollToId } from './ui.jsx';

const NIGHT = { bg: '#070A0E', dot: 'rgba(196,202,212,.10)', rail: 'rgba(196,202,212,.14)', part: 'rgba(230,234,242,.36)', pass: 'rgba(120,150,255,.8)', mark: 'rgba(230,234,242,.5)', br: '140,166,255', chip: '#8CA6FF', chipT: '#0B0F14', glow: '#3D63FF', beam: '#E6ECFF' };
const DAY = { bg: '#FFFFFF', dot: 'rgba(11,20,48,.12)', rail: 'rgba(11,20,48,.16)', part: 'rgba(11,20,48,.42)', pass: 'rgba(30,58,138,.85)', mark: 'rgba(11,20,48,.5)', br: '30,58,138', chip: '#1E3A8A', chipT: '#FFFFFF', glow: '#22D3B0', beam: '#0B9E83' };
const REJECT = '#FF5C6C';
const TYPES = ['hex', 'cap', 'board', 'can'];
const DEFECTS = ['scratch', 'crack', 'low fill', 'solder bridge', 'seal', 'dent', 'flash'];

const newPart = (x) => ({ x, type: TYPES[Math.floor(Math.random() * 4)], s: 24 + Math.random() * 16, defect: Math.random() < 0.13, done: false, t0: -1e9, vy: 0, dy: 0, label: '', gone: false });

function inspect(p, now) {
  p.done = true;
  p.t0 = now;
  p.label = p.defect
    ? 'REJECT · ' + DEFECTS[Math.floor(Math.random() * DEFECTS.length)] + ' ' + (0.82 + Math.random() * 0.16).toFixed(2)
    : 'PASS ' + (0.02 + Math.random() * 0.1).toFixed(2);
}

function drawShape(ctx, p, x, y) {
  const s = p.s;
  ctx.beginPath();
  if (p.type === 'hex') {
    for (let k = 0; k < 6; k++) { const a = Math.PI / 3 * k + Math.PI / 6; ctx[k ? 'lineTo' : 'moveTo'](x + Math.cos(a) * s / 2, y + Math.sin(a) * s / 2); }
    ctx.closePath(); ctx.moveTo(x + s * 0.2, y); ctx.arc(x, y, s * 0.2, 0, Math.PI * 2);
  } else if (p.type === 'cap') {
    ctx.arc(x, y, s / 2, 0, Math.PI * 2); ctx.moveTo(x + s * 0.3, y); ctx.arc(x, y, s * 0.3, 0, Math.PI * 2);
  } else if (p.type === 'board') {
    const w = s * 1.4, h = s * 0.85;
    ctx.rect(x - w / 2, y - h / 2, w, h); ctx.rect(x - w * 0.34, y - h * 0.22, w * 0.22, h * 0.44);
    ctx.rect(x + w * 0.08, y - h * 0.22, w * 0.12, h * 0.18); ctx.rect(x + w * 0.08, y + h * 0.06, w * 0.24, h * 0.16);
  } else {
    const w = s * 0.72, h = s * 1.15;
    if (ctx.roundRect) ctx.roundRect(x - w / 2, y - h / 2, w, h, 5); else ctx.rect(x - w / 2, y - h / 2, w, h);
    ctx.moveTo(x - w / 2, y - h * 0.28); ctx.lineTo(x + w / 2, y - h * 0.28);
  }
  ctx.stroke();
}

function drawBrackets(ctx, x, y, w, h, c) {
  const L = 8;
  ctx.strokeStyle = c; ctx.lineWidth = 1.5; ctx.beginPath();
  [[x, y, 1, 1], [x + w, y, -1, 1], [x, y + h, 1, -1], [x + w, y + h, -1, -1]].forEach(([a, b, dx, dy]) => {
    ctx.moveTo(a + dx * L, b); ctx.lineTo(a, b); ctx.lineTo(a, b + dy * L);
  });
  ctx.stroke();
}

function useInspectionCanvas(canvasRef, countRef, rejRef, msRef) {
  React.useEffect(() => {
    // Skip during prerender (puppeteer sets navigator.webdriver) so the
    // snapshot's HUD numbers match what React hydrates with.
    if (navigator.webdriver) return undefined;
    const cv = canvasRef.current;
    if (!cv) return undefined;
    const ctx = cv.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const S = { lanes: [], bx: 0, mx: null, count: 18342, rej: 41, t: performance.now(), W: 0, H: 0 };

    const build = () => {
      const r = cv.getBoundingClientRect();
      S.W = r.width; S.H = r.height;
      cv.width = S.W * dpr; cv.height = S.H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      S.bx = S.W * 0.7;
      const n = Math.max(3, Math.floor((S.H - 160) / 118));
      S.lanes = [];
      for (let i = 0; i < n; i++) {
        const y = 110 + (S.H - 250) * (i + 0.5) / n, sp = 34 + Math.random() * 46, parts = [];
        let x = -Math.random() * 200;
        while (x < S.W + 200) {
          const p = newPart(x);
          if (x > S.bx) { inspect(p, -1e9); if (p.defect) p.gone = true; }
          parts.push(p);
          x += 150 + Math.random() * 180;
        }
        S.lanes.push({ y, sp, parts, ph: 0 });
      }
    };
    const ro = new ResizeObserver(build);
    ro.observe(cv);
    build();

    const onMove = (e) => {
      const r = cv.getBoundingClientRect();
      S.mx = (e.clientY >= r.top && e.clientY <= r.bottom) ? e.clientX - r.left : null;
    };
    window.addEventListener('pointermove', onMove);

    let raf = 0;
    let visible = true;
    const io = new IntersectionObserver(([en]) => { visible = en.isIntersecting; });
    io.observe(cv);

    const loop = (now) => {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (now - S.t) / 1000);
      S.t = now;
      if (!visible) return;
      const { W, H } = S;
      const step = reduce ? 0 : dt;
      const target = S.mx != null ? Math.max(W * 0.38, Math.min(W - 30, S.mx)) : W * 0.7;
      S.bx += (target - S.bx) * (reduce ? 1 : Math.min(1, dt * 5));

      const C = document.documentElement.dataset.theme === 'day' ? DAY : NIGHT;
      ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, H);
      const off = (now * 0.006) % 32;
      ctx.fillStyle = C.dot;
      for (let gx = -off; gx < W; gx += 32) for (let gy = 8; gy < H; gy += 32) ctx.fillRect(gx, gy, 1.2, 1.2);
      const band = ctx.createLinearGradient(S.bx - 80, 0, S.bx + 80, 0);
      band.addColorStop(0, 'rgba(61,99,255,0)'); band.addColorStop(0.5, 'rgba(61,99,255,.16)'); band.addColorStop(1, 'rgba(61,99,255,0)');
      ctx.fillStyle = band; ctx.fillRect(S.bx - 80, 0, 160, H);

      ctx.font = "500 10px 'Google Sans Code', monospace";
      S.lanes.forEach((ln) => {
        ln.ph = (ln.ph + ln.sp * step) % 22;
        const ry = ln.y + 34;
        ctx.strokeStyle = C.rail; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(0, ry); ctx.lineTo(W, ry); ctx.stroke();
        ctx.beginPath(); for (let x = ln.ph - 22; x < W; x += 22) { ctx.moveTo(x, ry + 3); ctx.lineTo(x + 6, ry + 3); } ctx.stroke();
        let minX = Infinity;
        ln.parts.forEach((p) => { if (p.x < minX) minX = p.x; });
        ln.parts.forEach((p) => {
          p.x += ln.sp * step;
          if (p.x > W + 120) { Object.assign(p, newPart(minX - (150 + Math.random() * 180))); minX = p.x; }
          if (!p.done && p.x >= S.bx && p.x - ln.sp * step < S.bx + 1) {
            inspect(p, now);
            S.count++; if (p.defect) S.rej++;
            if (countRef.current) countRef.current.textContent = S.count.toLocaleString('en-US');
            if (rejRef.current) rejRef.current.textContent = S.rej;
            if (msRef.current) msRef.current.textContent = (38 + Math.floor(Math.random() * 9)) + ' ms';
          }
          if (p.gone) return;
          const age = now - p.t0;
          let alpha = 1;
          if (p.done && p.defect && age > 900) {
            p.vy += 700 * step; p.dy += p.vy * step;
            alpha = Math.max(0, 1 - p.dy / 120);
            if (alpha <= 0) { p.gone = true; return; }
          }
          const y = ln.y + p.dy;
          ctx.globalAlpha = alpha; ctx.lineWidth = 1.3;
          ctx.strokeStyle = !p.done ? C.part : p.defect ? REJECT : C.pass;
          drawShape(ctx, p, p.x, y);
          if (p.defect) {
            ctx.strokeStyle = p.done ? REJECT : C.mark; ctx.lineWidth = 1.2; ctx.beginPath();
            ctx.moveTo(p.x - p.s * 0.28, y - p.s * 0.1); ctx.lineTo(p.x - p.s * 0.08, y + p.s * 0.04);
            ctx.lineTo(p.x + p.s * 0.02, y - p.s * 0.08); ctx.lineTo(p.x + p.s * 0.22, y + p.s * 0.12); ctx.stroke();
          }
          if (p.done && (p.defect || age < 1500)) {
            const fa = p.defect ? 1 : Math.max(0, 1 - age / 1500);
            const c = p.defect ? REJECT : 'rgba(' + C.br + ',' + fa + ')';
            const bw = p.s * 1.4 + 18, bh = p.s * 1.15 + 18;
            drawBrackets(ctx, p.x - bw / 2, y - bh / 2, bw, bh, c);
            const tw = ctx.measureText(p.label).width + 10;
            ctx.globalAlpha = alpha * (p.defect ? 1 : fa);
            ctx.fillStyle = p.defect ? REJECT : C.chip; ctx.fillRect(p.x - bw / 2, y - bh / 2 - 18, tw, 15);
            ctx.fillStyle = C.chipT; ctx.fillText(p.label, p.x - bw / 2 + 5, y - bh / 2 - 7);
          }
          ctx.globalAlpha = 1;
        });
      });
      ctx.save();
      ctx.shadowColor = C.glow; ctx.shadowBlur = 20; ctx.strokeStyle = C.beam; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(S.bx, 74); ctx.lineTo(S.bx, H - 110); ctx.stroke();
      ctx.restore();
      ctx.fillStyle = C.beam;
      S.lanes.forEach((ln) => { ctx.beginPath(); ctx.moveTo(S.bx - 5, ln.y - 30); ctx.lineTo(S.bx + 5, ln.y - 30); ctx.lineTo(S.bx, ln.y - 24); ctx.fill(); });
      ctx.font = "500 11px 'Google Sans Code', monospace";
      ctx.fillText('CAM 01 · SCANNING', S.bx + 10, H - 116);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('pointermove', onMove);
    };
  }, [canvasRef, countRef, rejRef, msRef]);
}

export function Hero() {
  const canvasRef = React.useRef(null);
  const countRef = React.useRef(null);
  const rejRef = React.useRef(null);
  const msRef = React.useRef(null);
  useInspectionCanvas(canvasRef, countRef, rejRef, msRef);

  return (
    <section className="hero">
      <canvas ref={canvasRef} aria-hidden="true" />
      <div className="hero-fade" />
      <div className="wrap hero-body">
        <div className="hero-copy">
          <div className="hero-tag"><i /><div>AI VISUAL INSPECTION · MADE IN CANADA</div></div>
          <h1>Every part inspected. <span className="ac">Live by end of shift.</span></h1>
          <p className="hero-sub">VIAI checks every unit on your line in under 50 ms. Train it on 30 photos of good parts. Your QA team runs it.</p>
          <div className="hero-ctas">
            <ContactButton size="btn-lg">See it on your part →</ContactButton>
            <a href="#home-products" className="btn btn-ghost btn-lg"
               onClick={(e) => { e.preventDefault(); scrollToId('home-products'); }}>Explore the products</a>
          </div>
        </div>
      </div>
      <div className="hero-hud" aria-hidden="true">
        <div><div className="k">LINE A1 · CAM 01</div><div className="ac">● LIVE</div></div>
        <div><div className="k">Inspected</div><div ref={countRef}>18,342</div></div>
        <div><div className="k">Rejected</div><div ref={rejRef} style={{ color: 'var(--reject)' }}>41</div></div>
        <div><div className="k">Verdict in</div><div ref={msRef}>42 ms</div></div>
      </div>
      <div className="hero-stats">
        <div className="wrap grid g4">
          {HERO_STATS.map(s => (
            <div className="stat" key={s.l}>
              <div className="v">{s.v}<span>{s.u}</span></div>
              <div className="l">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
